import { LayoutList, Eye } from 'lucide-react';
import { ADMIN_GUARD } from '../admin-guard';
import { CONTEXTS, lazyView, type FeatureManifest } from '@/app/registry';
import { adminSkillUpRoutes } from './routes';
import { SKILL_UP_NODE_PREVIEW_TAB } from './skillUpPanels';

const navTab = {
  id: 'admin-nav',
  label: 'Navigation',
  icon: LayoutList,
  view: lazyView(() => import('../views/AdminNavView'), 'AdminNavView'),
};

const navPanel = { open: true, defaultActiveTab: 'admin-nav' } as const;

/**
 * Skill-Up authoring (SAAS-827) : content nodes CRUD, concepts CRUD and the
 * concept roadmap. Node deletion does not exist backend-side (SAAS-783 §2) —
 * the list exposes a "reject" action instead.
 */
export const adminSkillUpManifest: FeatureManifest = {
  id: 'admin-skill-up',
  contexts: [
    CONTEXTS.ADMIN_SKILL_UP_NODES,
    CONTEXTS.ADMIN_SKILL_UP_NODE_FORM,
    CONTEXTS.ADMIN_SKILL_UP_NODE_LINKS,
    CONTEXTS.ADMIN_SKILL_UP_CONCEPTS,
    CONTEXTS.ADMIN_SKILL_UP_CONCEPT_FORM,
    CONTEXTS.ADMIN_SKILL_UP_CONCEPT_ROADMAP,
  ],

  routes: [{ routes: adminSkillUpRoutes, errorFallbackLabel: 'Skill-Up', guard: ADMIN_GUARD }],

  centerView: {
    [CONTEXTS.ADMIN_SKILL_UP_NODES]: lazyView(
      () => import('../views/AdminSkillUpNodesListView'),
      'AdminSkillUpNodesListView',
    ),
    [CONTEXTS.ADMIN_SKILL_UP_NODE_FORM]: lazyView(
      () => import('../views/AdminSkillUpNodeFormView'),
      'AdminSkillUpNodeFormView',
    ),
    [CONTEXTS.ADMIN_SKILL_UP_NODE_LINKS]: lazyView(
      () => import('../views/AdminSkillUpNodeLinksView'),
      'AdminSkillUpNodeLinksView',
    ),
    [CONTEXTS.ADMIN_SKILL_UP_CONCEPTS]: lazyView(
      () => import('../views/AdminSkillUpConceptsListView'),
      'AdminSkillUpConceptsListView',
    ),
    [CONTEXTS.ADMIN_SKILL_UP_CONCEPT_FORM]: lazyView(
      () => import('../views/AdminSkillUpConceptFormView'),
      'AdminSkillUpConceptFormView',
    ),
    [CONTEXTS.ADMIN_SKILL_UP_CONCEPT_ROADMAP]: lazyView(
      () => import('../views/AdminConceptRoadmapView'),
      'AdminConceptRoadmapView',
    ),
  },

  panels: {
    left: {
      tabs: {
        [CONTEXTS.ADMIN_SKILL_UP_NODES]: [navTab],
        [CONTEXTS.ADMIN_SKILL_UP_NODE_FORM]: [navTab],
        [CONTEXTS.ADMIN_SKILL_UP_NODE_LINKS]: [navTab],
        [CONTEXTS.ADMIN_SKILL_UP_CONCEPTS]: [navTab],
        [CONTEXTS.ADMIN_SKILL_UP_CONCEPT_FORM]: [navTab],
        [CONTEXTS.ADMIN_SKILL_UP_CONCEPT_ROADMAP]: [navTab],
      },
    },
    right: {
      tabs: {
        [CONTEXTS.ADMIN_SKILL_UP_NODE_FORM]: [
          {
            id: SKILL_UP_NODE_PREVIEW_TAB,
            label: 'Aperçu',
            icon: Eye,
            view: lazyView(
              () => import('../views/AdminSkillUpNodePreviewView'),
              'AdminSkillUpNodePreviewView',
            ),
          },
        ],
      },
    },
  },

  enabledWhen: {
    roles: ['admin'],
  },

  panelRequirements: {
    [CONTEXTS.ADMIN_SKILL_UP_NODES]: { left: navPanel, right: { open: false } },
    [CONTEXTS.ADMIN_SKILL_UP_NODE_FORM]: {
      left: navPanel,
      // Live preview of the rendered template — always useful while editing.
      right: { open: true, defaultActiveTab: SKILL_UP_NODE_PREVIEW_TAB },
    },
    [CONTEXTS.ADMIN_SKILL_UP_NODE_LINKS]: { left: navPanel, right: { open: false } },
    [CONTEXTS.ADMIN_SKILL_UP_CONCEPTS]: { left: navPanel, right: { open: false } },
    [CONTEXTS.ADMIN_SKILL_UP_CONCEPT_FORM]: { left: navPanel, right: { open: false } },
    [CONTEXTS.ADMIN_SKILL_UP_CONCEPT_ROADMAP]: { left: navPanel, right: { open: false } },
  },
};
